/**
 * WhatsNewProvider — decides whether the release notes are owed to this agent.
 *
 * The app updates itself in the background (see ReloadPrompt), so an agent can
 * arrive at a changed screen having been told nothing. This announces the change
 * ONCE, on the launch after the update lands, with a short pop-up that leads to
 * the What's new page (/updates) — the page itself is always on More (the avatar
 * menu on desktop). The last acknowledged version is kept in localStorage.
 *
 * A first install acknowledges the current version silently. Someone who has
 * never seen an older build has missed nothing, and opening on a changelog is a
 * poor first screen.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LATEST_VERSION, unseenSince } from '../lib/releaseNotes';

const WhatsNewContext = createContext(null);

const SEEN_KEY = 'cid-agent-whatsnew-seen';

// Stands for "was already using CIDAS before the notes existed". Any version the
// list doesn't know announces the current release only (see unseenSince), which
// is what these agents want: they lived through the older ones, and the History
// view is there if they want to read back.
const PRE_NOTES = '0.0.0';

const readSeen = () => {
  try {
    return localStorage.getItem(SEEN_KEY);
  } catch {
    // private mode — treated as a fresh install, so nothing is announced
    return null;
  }
};

/**
 * Has this browser run CIDAS before?
 *
 * With no seen-version stored we cannot tell a brand-new install from an agent
 * who has been using the app since before these notes existed — and getting it
 * wrong the first way means the entire firm is silently marked up to date and
 * nobody is told about the release that introduced this. So the traces of a
 * previous run answer it: a session, or a nudge they have already dismissed.
 *
 * `cid_agent_theme` is deliberately NOT one of them — the theme provider writes
 * it on every launch, including the first, so it says nothing.
 */
const usedBefore = () => {
  try {
    return [
      'cid_agent_access_token', 'cid_agent_refresh_token', 'cid_agent_user',
      'cid-agent-install-dismissed', 'cid-agent-push-dismissed',
    ].some((k) => localStorage.getItem(k));
  } catch {
    return false;
  }
};

export function WhatsNewProvider({ children }) {
  // `null` until the first effect reads storage, so nothing renders on the
  // strength of "no version stored" before we have actually looked.
  const [seen, setSeen] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const stored = readSeen();
    if (stored) {
      setSeen(stored);
    } else if (usedBefore()) {
      // An existing agent arriving at this release for the first time. Nothing is
      // recorded here — `markSeen()` writes the acknowledgement, so an agent who
      // relaunches before reading the notes is shown them again.
      setSeen(PRE_NOTES);
    } else {
      try { localStorage.setItem(SEEN_KEY, LATEST_VERSION); } catch { /* private mode */ }
      setSeen(LATEST_VERSION);
    }
    setReady(true);
  }, []);

  const unseen = useMemo(() => (ready ? unseenSince(seen) : []), [ready, seen]);

  const markSeen = useCallback(() => {
    try { localStorage.setItem(SEEN_KEY, LATEST_VERSION); } catch { /* private mode */ }
    setSeen(LATEST_VERSION);
  }, []);

  // Closing the pop-up or opening the page acknowledges the update: the notes
  // were on screen either way.
  const value = { unseen, markSeen };

  return <WhatsNewContext.Provider value={value}>{children}</WhatsNewContext.Provider>;
}

export function useWhatsNew() {
  const ctx = useContext(WhatsNewContext);
  if (!ctx) throw new Error('useWhatsNew must be used within a WhatsNewProvider');
  return ctx;
}

export default WhatsNewContext;
